import { create } from "zustand";
import { create as createMessage, type MessageInitShape } from "@bufbuild/protobuf";
import { EnvelopeSchema, type Envelope } from "../gen/helm/v1/envelope_pb";
import { EMPTY_TRADING, reduceTrading, type TradingState } from "./state";

export type { AccountUpdate, FillEvent, OrderEvent, PositionUpdate } from "../gen/helm/v1/trading_pb";
export type { RiskState } from "../gen/helm/v1/risk_pb";
export type { LoginStatus, SessionState } from "../gen/helm/v1/session_pb";
export {
  BracketRole,
  LoginConnState,
  LoginRetryState,
  OrderState,
  OrderType,
  RiskStatus,
  SessionStatus,
  Side,
} from "../gen/helm/v1/common_pb";

/**
 * The window's trading state (accounts, orders, fills, positions, risk), fed by the trading
 * socket. Sits next to `useConnection` (stream/store.ts); see `trading/state.ts` for how replayed
 * and snapshot frames are applied.
 */
export const useTrading = create<TradingState>(() => EMPTY_TRADING);

/** Apply one trading-socket frame. */
export function applyTradingFrame(env: Envelope): void {
  const cur = useTrading.getState();
  const next = reduceTrading(cur, env);
  if (next !== cur) useTrading.setState(next, true);
}

/** Forget everything (sign-out). */
export function resetTrading(): void {
  useTrading.setState(EMPTY_TRADING, true);
}

/**
 * Build a trading-socket frame from init shapes (unset fields get proto defaults). For the
 * dev-only preview's synthetic feed, which then goes through the same `applyTradingFrame` path.
 */
export function tradingFrame(
  seq: bigint,
  body: NonNullable<MessageInitShape<typeof EnvelopeSchema>["body"]>,
): Envelope {
  return createMessage(EnvelopeSchema, { seq, reqId: "", body });
}

/** Feed the store from a stream (`TradingStreamClient.subscribe`); returns the unsubscribe. */
export function bindTradingState(stream: { subscribe(l: (env: Envelope) => void): () => void }): () => void {
  return stream.subscribe(applyTradingFrame);
}
